// JOBE-0105: ProjectsIndexPage — top-level route component for
// `/projects`. Phase 4 of the ADR-0023 projects-view rollout; the first
// user-visible page of the projects surface.
//
// Composition (per spec §Decision 5):
//   - useEntityBrowserState() gives us chip / sort / cursor state. The
//     Index is a single-instance page so we use the hook's default
//     unprefixed URL keys (no urlStateNamespace) — same shape the
//     library page uses. URLs read `?scope=mine&owner=alice` rather
//     than the namespaced `?scope=mine&projects.owner=alice`.
//   - The hook's `rows / kindSummary / loadState / error / retry` are
//     ignored (the hook fetches `/api/entities` which is dead weight on
//     this surface) — the page runs its own `useProjectsListQuery`
//     against `/api/projects?scope=…&expand=rollups` directly.
//   - `?scope=` is owned by the outer page (not by the hook's URL
//     state) per JOBE-0101 Decision 2 §State ownership.
//   - There is no pinned `type=project` chip: the `/api/projects`
//     endpoint is project-only by definition, so a type filter is
//     redundant. Dropping the pinned chip also drops the matching
//     visual noise (a non-removable chip) and the redundant URL key.

import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEntityBrowserState } from '../entitybrowser/useEntityBrowserState.js';
import { useStore } from '../lib/store.js';

import { ScopeToggle, SCOPE_OPTIONS } from './ScopeToggle.jsx';
import { ProjectsTable } from './ProjectsTable.jsx';
import { ByOwnerBlock } from './rail/ByOwnerBlock.jsx';
import { ByDepartmentBlock } from './rail/ByDepartmentBlock.jsx';
import { RecentActivityBlock } from './rail/RecentActivityBlock.jsx';
import { useProjectsListQuery } from './useProjectsListQuery.js';

import './projects.css';
import './projects-index.css';

const VALID_SCOPES = new Set(SCOPE_OPTIONS.map((o) => o.value));

export function ProjectsIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeRaw = searchParams.get('scope') || 'mine';
  const scope = VALID_SCOPES.has(scopeRaw) ? scopeRaw : 'mine';

  // The hook owns chip / sort / cursor state on the (unprefixed) URL.
  // We deliberately ignore the hook's `rows / loadState / error / retry`
  // (it fetches /api/entities, which is dead weight here).
  const browser = useEntityBrowserState();
  const {
    chips,
    sort,
    cursor,
    setChips,
    setSort,
    setCursor,
    selectedUrn,
    setSelectedUrn,
  } = browser;

  // Scope toggle URL writer. Defaults-to-mine: writing `mine` deletes
  // the `?scope=` key (per JOBE-0104's omitted-defaults-to-mine
  // contract). Changing scope clears the cursor (scope changes the
  // result-set source, so paging restarts from page 0).
  const onChangeScope = useCallback((next) => {
    if (!VALID_SCOPES.has(next)) return;
    const merged = new URLSearchParams(searchParams);
    if (next === 'mine') {
      merged.delete('scope');
    } else {
      merged.set('scope', next);
    }
    // Cursor lives under the unprefixed `cursor` key — drop it on
    // scope change so paging restarts from page 0.
    merged.delete('cursor');
    setSearchParams(merged, { replace: true });
    setCursor(null);
  }, [searchParams, setSearchParams, setCursor]);

  // Roster for the chip composer's PeopleValuePopover (the ChipRow
  // requires it). Mirror the library's roster shape.
  const agentsList = useStore((s) => s.agents.list);
  const roster = useMemo(
    () => ({ humans: [], agents: agentsList || [] }),
    [agentsList],
  );

  // ── Main fetch ──────────────────────────────────────────────────
  const {
    rows, nextCursor, loadState, error, retry, rollupsTruncated,
  } = useProjectsListQuery({
    scope,
    chips,
    sort,
    cursor,
  });

  // Selection — local-only; row click also navigates away (no inspector
  // in v1). We still wire setSelectedUrn so the row gets a visual --hi
  // treatment between click + navigation paint.
  const onSelect = useCallback((urn) => {
    setSelectedUrn(urn);
  }, [setSelectedUrn]);

  const onLoadMore = useCallback(() => {
    if (!nextCursor) return;
    setCursor(nextCursor);
  }, [nextCursor, setCursor]);

  // Rail click → append chip to host state. Uses the same canonical
  // merge as the library (one chip per friendlyToken; dedupe values).
  const appendChip = useCallback((parsed) => {
    setChips((prev) => {
      const idx = prev.findIndex(
        (c) => c?.kind === 'predicate' && c.friendlyToken === parsed.friendlyToken,
      );
      if (idx >= 0) {
        const merged = {
          ...prev[idx],
          values: Array.from(new Set([...(prev[idx].values || []), ...(parsed.values || [])])),
        };
        return prev.map((c, i) => (i === idx ? merged : c));
      }
      return [...prev, parsed];
    });
  }, [setChips]);

  return (
    <div className="projects-index-page" data-testid="projects-index-page">
      <header className="projects-index-header">
        <div className="projects-index-titlebar">
          <h1 className="projects-index-title">Projects</h1>
          <p className="projects-index-lede muted">
            Browse projects in scope. Click any project to open its home.
          </p>
        </div>
        <div className="projects-index-header-actions">
          <ScopeToggle value={scope} onChange={onChangeScope} />
          <button
            type="button"
            className="projects-new-cta"
            data-testid="projects-new-cta"
            disabled
            title="Create project — coming soon"
            onClick={() => { /* stub */ }}
          >
            + New project
          </button>
        </div>
      </header>

      <div className="projects-index-grid">
        <main className="projects-index-main">
          <ProjectsTable
            rows={rows}
            nextCursor={nextCursor}
            loadState={loadState}
            error={error}
            rollupsTruncated={rollupsTruncated}
            chips={chips}
            onChipsChange={setChips}
            onLoadMore={onLoadMore}
            onRetry={retry}
            selectedUrn={selectedUrn}
            onSelect={onSelect}
            roster={roster}
          />
        </main>
        <aside
          className="projects-index-rail"
          data-testid="projects-index-rail"
          aria-label="Projects index side rail"
        >
          <ByOwnerBlock
            rows={rows}
            onAppendChip={appendChip}
            roster={roster}
          />
          <ByDepartmentBlock
            rows={rows}
            onAppendChip={appendChip}
          />
          <RecentActivityBlock />
        </aside>
      </div>
    </div>
  );
}
